/**
 * nextQuestionEngine.js — pick the unanswered question with the highest
 * expected information value for the current candidate pool.
 *
 * Practical heuristic (not full information theory, per design brief):
 *
 *   value = (expected eliminations + resolution value) × stage weight ÷ friction
 *
 *  - expected eliminations: for enumerable questions, simulate every possible
 *    answer against the surviving products and average how many would be
 *    eliminated (questions that split the pool score highest).
 *  - resolution value: how many surviving products currently sit in
 *    needs_data waiting on an input this question resolves.
 *  - stage weight: core branch questions before intent refinements.
 *  - friction: cheap taps beat questions that require thought.
 *
 * Intent questions gain value as the eligible pool shrinks — once few
 * products remain, distinguishing them matters more than eliminating.
 */
import { evaluateEligibility } from './eligibilityEngine.js';
import { QUESTION_REGISTRY, STAGE_WEIGHT } from './questionRegistry.js';

function alive(evaluations) {
  return evaluations.filter((e) => e.status === 'eligible' || e.status === 'needs_data');
}

/** Context handed to appliesWhen: what kinds of products survive. */
export function buildContext(evaluations) {
  const live = alive(evaluations);
  return {
    remaining: live,
    armAlive: live.some((e) => e.product.structure.kind === 'arm'),
    fixedTermsAlive: [...new Set(
      live.filter((e) => e.product.structure.kind === 'fixed').map((e) => e.product.structure.termYears)
    )],
  };
}

/**
 * @param {import('./borrowerProfile').BorrowerProfile} profile
 * @param {Array} evaluations current eligibility evaluations
 * @param {Set<string>} answeredKeys question keys already asked/answered
 * @returns {{ key, score, expectedEliminations }|null}
 */
export function selectNextQuestion(profile, evaluations, answeredKeys) {
  const live = alive(evaluations);
  if (!live.length) return null;
  const ctx = buildContext(evaluations);
  const liveProducts = live.map((e) => e.product);
  const eligibleCount = evaluations.filter((e) => e.status === 'eligible').length;

  // Intent questions matter more once the pool is small
  const intentBoost = live.length <= 25 ? 2.2 : live.length <= 60 ? 1.4 : 1;

  let best = null;
  for (const q of QUESTION_REGISTRY) {
    if (answeredKeys.has(q.key)) continue;
    if (q.appliesWhen && !q.appliesWhen(profile, ctx)) continue;

    // Resolution value: products stuck in needs_data on inputs this resolves
    const resolvable = live.filter((e) => e.missingInputs.some((k) => q.resolves.includes(k))).length;

    // Expected eliminations by simulating each enumerable answer
    let expectedEliminations = 0;
    if (q.values && q.simulate) {
      let total = 0;
      for (const v of q.values) {
        const what = { ...profile, ...q.simulate(profile, v) };
        const evals = evaluateEligibility(what, liveProducts);
        total += evals.filter((e) => e.status === 'ineligible').length;
      }
      expectedEliminations = total / q.values.length;
    } else {
      // Currency/free inputs: value comes from unblocking needs_data products
      expectedEliminations = resolvable * 0.35;
    }

    let stageWeight = STAGE_WEIGHT[q.stage] || 1;
    if (q.stage === 'intent' || q.stage === 'conditional') stageWeight *= intentBoost;

    // If nothing is eligible yet, intent questions can wait
    if ((q.stage === 'intent' || q.stage === 'conditional') && eligibleCount === 0 && resolvable === 0) continue;

    const score = ((expectedEliminations + resolvable * 0.6 + 0.5) * stageWeight) / q.friction;
    if (!best || score > best.score) {
      best = { key: q.key, score, expectedEliminations: Math.round(expectedEliminations) };
    }
  }
  return best;
}
